import React, { memo, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { StripeCheckOutButton } from '../../buttons/StripeCheckOutButton'
import ButtonCustom from '../../atoms/button/ButtonCustom'
import { getDaysRemaining } from '../../../util/getTime'
import SwitchBtn from '../../atoms/switch-btn/SwicthBtn'

import {
	getSubscriptionPlans,
	cancelSubscription,
	generateInvoice,
	getPaymentMethods,
	removePaymentMethod,
	purchaseSubscriptionPlan,
	getActiveSubscriptionStatus,
} from '../../../features/v2/subscriptionSlice'
import CommonDeleteModal from '../../atoms/modal/CommonDeleteModal'

import styles from './subscriptionTab.module.scss'
import CommonModal from '../../atoms/modal/CommonModal'
import Grid from '@mui/material/Grid'
import CardIcon from '@mui/icons-material/CreditCard'
import {
	closeModal,
	openModal,
	setModalData,
} from '../../../features/commonSlice'
import { toastHelper } from '../../../util/toastHelper'
import { PageLoadingSpinner } from '../../loadingSpinner/LoadingSpinner'
import { checkAuth } from '../../../features/authSlice'
import { PLAN_DETAILS } from '../../../util/constants'

const FeatureItem = memo(({ children, comingSoon = false }) => (
	<li className={styles.sspFeature}>
		{comingSoon ? (
			<img src="/images/time-icon.svg" alt="time" />
		) : (
			<img src="/images/SelectCheck.svg" alt="check" />
		)}
		<span
			className={`text-base ${comingSoon ? 'text-gray-500 italic' : 'text-gray-700'}`}
		>
			{children}
		</span>
	</li>
))

const PlanCard = memo(
	({
		plan,
		accountType,
		subscriptionEndDate,
		isUserLifetimePlan,
		subscriptionId,
		showPaymentMethodsModal,
		isRecurringSubscription,
		payment,
		paymentExpiresAt,
		hasAnyActivePlan,
		isBadgeActive,
	}) => {
		const dispatch = useDispatch()
		const [isCancelling, setIsCancelling] = useState(false)
		const [isDownloading, setIsDownloading] = useState(false)
		const [showCancelModal, setShowCancelModal] = useState(false)

		if (!plan) return null

		const isLifetimePlan =
			accountType === 'user' &&
			plan.id === 'premium' &&
			(isUserLifetimePlan || payment === 'premium-wedding-access')

		const isUserPremiumPlan = accountType === 'user' && plan.id === 'premium'
		const isUserOnMonthlyPremium = isUserPremiumPlan && payment === 'premium-wedding-access-month'

		const planDetails = useMemo(() => {
			if (plan.id === 'top_badge') {
				return {
					id: 'top_badge',
					features: {
						included: [
							'Detailed profile with up to 10 photos',
							'List up to 3 packages (coming soon)',
							'Top label badge on profile',
							'Appear at the top of listings in marketplace',
						],
					},
				}
			}
			return PLAN_DETAILS[accountType]?.find((p) => p.id === plan.id)
		}, [accountType, plan.id])

		const priceDisplay = useMemo(() => {
			if (plan.id === 'free') {
				return { amount: '$0', duration: '/ forever' }
			}

			if (plan.price) {
				const amount = plan.price.amount
				const currency = plan.price.currency?.toUpperCase() || 'CAD'
				const billing = plan.price.billing

				if (billing === 'month') {
					return {
						amount: `$${amount}`,
						duration: `${currency}/ month`,
					}
				} else if (['year', 'yearly'].includes(billing)) {
					return {
						amount: `$${amount}`,
						duration: `${currency}/ year`,
					}
				} else if (billing === 'life-time') {
					return {
						amount: `$${amount}`,
						duration: `${currency}/ life-time`,
					}
				}
			}

			return { amount: '', duration: '' }
		}, [plan.id, plan.price])

		const getPaymentTierForPlan = (planId, planTier, accountType) => {
			if (planId === 'premium' && accountType === 'user') {
				return ['premium-wedding-access', 'premium-wedding-access-month']
			}
			if (planId === 'business_basics') {
				return ['business-basic', 'business-basic-month']
			}
			if (planId === 'business_pro') {
				return ['business-pro', 'business-pro-month']
			}
			if (planId === 'top_badge') {
				return accountType === 'vendor'
					? ['top-vendor']
					: ['top-planner']
			}
			return [planTier]
		}

		const isActive = useMemo(() => {
			if (plan.id === 'free') {
				return !hasAnyActivePlan
			}

			if (isLifetimePlan && plan.id === 'premium' && accountType === 'user') {
				return true
			}

			if (plan.id === 'top_badge') {
				return isBadgeActive ?? false
			}

			if (!payment || payment === 'free-tier') {
				return false
			}

			const validTiers = getPaymentTierForPlan(plan.id, plan.tier, accountType)
			const paymentMatches = validTiers.includes(payment)

			if (!paymentMatches) {
				return false
			}

			if (paymentExpiresAt === null) {
				return true
			}

			const today = new Date()
			const endDate = new Date(paymentExpiresAt)
			return endDate > today
		}, [payment, paymentExpiresAt, plan.id, plan.tier, accountType, isLifetimePlan, hasAnyActivePlan, isBadgeActive])

		const expirationInfo = useMemo(() => {
			if (!isActive) return null

			if (isLifetimePlan || paymentExpiresAt === null) {
				return {
					isLifetime: true,
					text: 'Lifetime access',
				}
			}

			if (subscriptionEndDate) {
				const formattedDate = typeof subscriptionEndDate === 'string'
					? subscriptionEndDate.slice(0, 10)
					: new Date(subscriptionEndDate).toISOString().slice(0, 10)
				const timeRemaining = getDaysRemaining(subscriptionEndDate)
				return {
					isLifetime: false,
					formattedDate,
					timeRemaining,
				}
			}

			return null
		}, [isActive, isLifetimePlan, subscriptionEndDate, paymentExpiresAt])

		const handleDownloadInvoice = async () => {
			if (!subscriptionId) return

			setIsDownloading(true)
			try {
				const result = await dispatch(
					generateInvoice(subscriptionId)
				).unwrap()
				if (result?.success && result?.data) {
					window.open(result.data, '_blank')
				}
			} catch (error) {
				console.error('Failed to download invoice:', error)
			} finally {
				setIsDownloading(false)
			}
		}

		const handleOpenCancelModal = () => {
			setShowCancelModal(true)
		}

		const handleCloseCancelModal = () => {
			setShowCancelModal(false)
		}

		const handleConfirmCancelSubscription = async () => {
			if (!subscriptionId) return

			setIsCancelling(true)
			try {
				await dispatch(cancelSubscription({ subscriptionId })).unwrap()
				setShowCancelModal(false)
				await dispatch(getActiveSubscriptionStatus()).unwrap()
				dispatch(checkAuth()).unwrap()
			} catch (error) {
				console.error('Failed to cancel subscription:', error)
			} finally {
				setIsCancelling(false)
			}
		}

		const features = planDetails?.features?.included || []
		const bestFor = planDetails?.best_for
		const notes = planDetails?.notes

		const sectionTitle =
			planDetails?.features?.includes_all_free ||
				planDetails?.features?.includes_all_business_basics
				? `Everything in ${plan.id === 'business_pro' ? 'Business Basics' : 'Free'}, plus:`
				: 'Includes:'

		return (
			<article className={styles.planCard}>
				<h3 className={styles.planTitle}>
					{plan.name || plan.title || 'Plan'}
				</h3>
				{plan.description && (
					<div className={styles.planDesc}>
						<strong>{plan.description}</strong>
					</div>
				)}
				{plan.title && plan.title !== plan.description && (
					<div className={styles.planDesc}>{plan.title}</div>
				)}
				<div className={styles.price}>
					{isUserPremiumPlan && !isUserOnMonthlyPremium ? '$39' : priceDisplay.amount}
					{priceDisplay.duration && (
						<span className={styles.sspDuration}>
							{isUserPremiumPlan && !isUserOnMonthlyPremium
								? 'CAD/ one time'
								: priceDisplay.duration}
						</span>
					)}
				</div>
				{isActive && <div className={styles.currentBadge}>Active</div>}
				{expirationInfo && (
					<div
						className={styles.bestFor}
						style={{ marginTop: '0.5em' }}
					>
						{expirationInfo.isLifetime ? (
							<strong>{expirationInfo.text}</strong>
						) : (
							<>
								<strong>Expires:</strong>{' '}
								{expirationInfo.formattedDate} (
								{expirationInfo.timeRemaining})
							</>
						)}
					</div>
				)}
				{features.length > 0 && (
					<>
						<div className={styles.sectionTitle}>
							{sectionTitle}
						</div>
						<ul className={styles.sspFeatures}>
							{features.map((feature, idx) => {
								const comingSoon =
									feature.includes('(coming soon)')
								const cleanFeature = feature
									.replace('(coming soon)', '')
									.trim()
								return (
									<FeatureItem
										key={idx}
										comingSoon={comingSoon}
									>
										{cleanFeature}
									</FeatureItem>
								)
							})}
						</ul>
					</>
				)}
				{planDetails?.features?.not_included?.length > 0 && (
					<>
						<div className={styles.sectionTitle}>Not Included:</div>
						<ul className={styles.sspFeatures}>
							{planDetails?.features?.not_included?.map(
								(feature, idx) => {
									return (
										<FeatureItem key={idx}>
											{feature}
										</FeatureItem>
									)
								}
							)}
						</ul>
					</>
				)}
				{bestFor && (
					<div className={styles.bestFor}>
						<strong>Best for:</strong> {bestFor}
					</div>
				)}
				{notes && notes.length > 0 && (
					<div className={styles.bestFor}>
						{notes.map((note, idx) => (
							<div
								key={idx}
								style={{ marginTop: idx > 0 ? '8px' : 0 }}
							>
								{note}
							</div>
						))}
					</div>
				)}
				{plan.id !== 'free' &&
					!isActive &&
					(plan.id === 'top_badge' ? true : !hasAnyActivePlan) &&
					!showPaymentMethodsModal && (
						<div className={styles.sspCta}>
							<StripeCheckOutButton
								data={
									isUserPremiumPlan
										? {
											items: [
												{
													name: 'Premium Wedding Access',
													price: 39,
													quantity: 1,
												},
											],
											accountType,
											subscriptionTier:
												'premium-wedding-access',
										}
										: {
											priceId:
												plan.stripeProductPriceId,
											accountType,
											subscriptionTier: plan.tier,
											subscriptionPlanId:
												plan.subscriptionPlanId,
										}
								}
								label={`Upgrade to ${plan.name}`}
								className={styles.sspBtn}
							/>
						</div>
					)}
				{plan.id !== 'free' && showPaymentMethodsModal && (
					<div className={styles.sspCta}>
						<ButtonCustom
							size="md"
							className={styles.sspBtn}
							onClick={() => {
								dispatch(openModal('paymentMethodsModal'))
								dispatch(setModalData({ ...plan, accountType }))
							}}
							disabled={(plan.id === 'top_badge' ? isBadgeActive : hasAnyActivePlan)}
						>
							Manage Payment Methods
						</ButtonCustom>
					</div>
				)}
				{isActive && !isLifetimePlan && subscriptionId && (
					<div className={`${styles.sspCta} ${styles.sspCtaButtons}`}>
						<ButtonCustom
							size="md"
							className={styles.sspBtn}
							onClick={handleDownloadInvoice}
							disabled={isDownloading}
						>
							{isDownloading
								? 'Downloading...'
								: 'Download Invoice'}
						</ButtonCustom>
						{((payment && payment.endsWith('-month')) || plan.id === 'top_badge') && (
							isRecurringSubscription ? (
								<ButtonCustom
									variant="destructive"
									size="md"
									className={styles.sspBtn}
									onClick={handleOpenCancelModal}
									disabled={isCancelling}
								>
									{isCancelling
										? 'Processing...'
										: 'Cancel Subscription'}
								</ButtonCustom>
							) : (
								<div className={styles.sspBtn} style={{
									padding: '10px 20px',
									textAlign: 'center',
									color: 'var(--secondary-Text)',
									fontSize: '14px'
								}}>
									Cancelled - active until {subscriptionEndDate ? (typeof subscriptionEndDate === 'string'
										? subscriptionEndDate.slice(0, 10)
										: new Date(subscriptionEndDate).toISOString().slice(0, 10)) : 'N/A'}
								</div>
							)
						)}
					</div>
				)}
				<CommonDeleteModal
					open={showCancelModal}
					handleClose={handleCloseCancelModal}
					heading="Are you sure you want to cancel your subscription?"
					subHeading="It will remain active until the end of the current billing period."
					footerContent={
						<>
							<ButtonCustom
								onClick={handleCloseCancelModal}
								disabled={isCancelling}
							>
								No
							</ButtonCustom>
							<ButtonCustom
								variant="destructive"
								onClick={handleConfirmCancelSubscription}
								disabled={isCancelling}
							>
								{isCancelling ? 'Processing...' : 'Yes'}
							</ButtonCustom>
						</>
					}
				/>
			</article>
		)
	}
)

const PaymentMethodsModal = memo(({ open }) => {
	const dispatch = useDispatch()
	const data = useSelector((s) => s.common.data)
	const paymentMethods = useSelector((s) => s.subscription.paymentMethods)
	const [isProcessing, setIsProcessing] = useState(false)

	const handleRemovePaymentMethod = async (pmId) => {
		setIsProcessing(true)
		try {
			await dispatch(removePaymentMethod(pmId)).unwrap()
		} catch (err) {
			toastHelper(
				err.message || 'Failed to remove payment method',
				'error'
			)
		} finally {
			setIsProcessing(false)
		}
	}

	const handleProcessPaymentMethod = async (pmId) => {
		setIsProcessing(true)
		try {
			const result = await dispatch(
				purchaseSubscriptionPlan({
					priceId: data.stripeProductPriceId,
					accountType: data.accountType,
					subscriptionTier: data.tier,
					subscriptionPlanId: data.subscriptionPlanId,
					paymentMethodId: pmId,
				})
			).unwrap()

			if (!result?.success)
				throw new Error(
					result?.message || 'Failed to purchase subscription'
				)
			toastHelper(
				result?.message || 'Subscription purchased successfully',
				'success'
			)
			handleClose()
			await dispatch(checkAuth()).unwrap()
		} catch (err) {
			toastHelper(
				err.message || 'Failed to purchase subscription',
				'error'
			)
		} finally {
			setIsProcessing(false)
		}
	}

	const handleClose = () => {
		dispatch(closeModal())
		dispatch(setModalData(null))
	}

	if (!open || !data) return null

	if (isProcessing) return <PageLoadingSpinner />

	return (
		<CommonModal
			open={open}
			handleClose={handleClose}
			heading="Payment Methods"
			subHeading="Select a payment method to process your subscription."
			footerContent={
				<StripeCheckOutButton
					data={{
						priceId: data.stripeProductPriceId,
						accountType: data.accountType,
						subscriptionTier: data.tier,
						subscriptionPlanId: data.subscriptionPlanId,
					}}
					label={`Process with new payment method`}
					className={`${styles.sspBtn} ${styles.paymentMethodsModalBtn}`}
				/>
			}
			modalInner={styles.paymentMethodsModalInner}
		>
			{paymentMethods?.length > 0 ? (
				<div className={styles.paymentMethodsContainer}>
					<Grid container spacing={3}>
						{paymentMethods.map((pm) => (
							<Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={pm.id}>
								<div
									style={{
										border: '1px solid var(--border)',
										borderRadius:
											'var(--border-radius-large)',
										padding: '20px',
										background: '#ffffff',
										display: 'flex',
										flexDirection: 'column',
										gap: '16px',
										height: '100%',
										position: 'relative',
										boxShadow:
											'0 2px 8px rgba(0, 0, 0, 0.08)',
										transition: 'all 0.2s ease',
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.boxShadow =
											'0 4px 12px rgba(0, 0, 0, 0.12)'
										e.currentTarget.style.transform =
											'translateY(-2px)'
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.boxShadow =
											'0 2px 8px rgba(0, 0, 0, 0.08)'
										e.currentTarget.style.transform =
											'translateY(0)'
									}}
								>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											marginBottom: '8px',
										}}
									>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '12px',
											}}
										>
											<div
												style={{
													fontSize: '32px',
													lineHeight: 1,
												}}
											>
												<CardIcon />
											</div>
											<div>
												<div
													style={{
														font: 'var(--body-font-two)',
														fontWeight: 700,
														color: 'var(--primary-Text)',
														textTransform:
															'capitalize',
													}}
												>
													{pm.brand || 'Card'}
												</div>
												<div
													style={{
														font: 'var(--body-font-three)',
														color: 'var(--secondary-Text)',
														marginTop: '2px',
													}}
												>
													•••• •••• •••• {pm.last4}
												</div>
											</div>
										</div>
									</div>

									<div
										style={{
											display: 'flex',
											flexDirection: 'column',
											gap: '8px',
											paddingTop: '12px',
											borderTop:
												'1px solid var(--border)',
										}}
									>
										<div
											style={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
											}}
										>
											<span
												style={{
													font: 'var(--body-font-three)',
													color: 'var(--secondary-Text)',
												}}
											>
												Expires:
											</span>
											<span
												style={{
													font: 'var(--body-font-three)',
													fontWeight: 600,
													color: 'var(--primary-Text)',
												}}
											>
												{String(pm.exp_month).padStart(
													2,
													'0'
												)}
												/{pm.exp_year}
											</span>
										</div>
										{pm.country && (
											<div
												style={{
													display: 'flex',
													justifyContent:
														'space-between',
													alignItems: 'center',
												}}
											>
												<span
													style={{
														font: 'var(--body-font-three)',
														color: 'var(--secondary-Text)',
													}}
												>
													Country:
												</span>
												<span
													style={{
														font: 'var(--body-font-three)',
														fontWeight: 600,
														color: 'var(--primary-Text)',
														textTransform:
															'uppercase',
													}}
												>
													{pm.country}
												</span>
											</div>
										)}
									</div>

									<div
										style={{
											display: 'flex',
											gap: '10px',
											marginTop: 'auto',
											paddingTop: '12px',
										}}
									>
										<ButtonCustom
											variant="default"
											size="md"
											onClick={() =>
												handleProcessPaymentMethod(
													pm.id
												)
											}
											style={{ flex: 1 }}
										>
											Process
										</ButtonCustom>
										<ButtonCustom
											variant="destructive"
											size="md"
											onClick={() =>
												handleRemovePaymentMethod(pm.id)
											}
											style={{ flex: 1 }}
										>
											Remove
										</ButtonCustom>
									</div>
								</div>
							</Grid>
						))}
					</Grid>
				</div>
			) : (
				<div
					style={{
						textAlign: 'center',
						padding: '40px 20px',
						color: 'var(--secondary-Text)',
						font: 'var(--body-font-three)',
					}}
				>
					No payment methods found. Add a payment method to get
					started.
				</div>
			)}
		</CommonModal>
	)
})

const SubscriptionPages = () => {
	const dispatch = useDispatch()
	const accountType = useSelector((s) => s.auth.accountType)
	const payment = useSelector((s) => s.auth.user?.payment)
	const paymentExpiresAt = useSelector((s) => s.auth.user?.paymentExpiresAt)
	const subscriptionStatus = useSelector((s) => s.subscription.subscriptionStatus)
	const plans = useSelector((s) => s.subscription.plans)
	const isVendorOrPlanner = ['vendor', 'kazoo-planner'].includes(accountType)
	const isUserLifetimePlan = useSelector(
		(s) => s.auth.user?.is_purchase_lifetime_plan
	)
	const paymentMethods = useSelector((s) => s.subscription.paymentMethods)
	const modal = useSelector((s) => s.common.modal)
	const topBadge = useSelector((s) => s.auth.user?.topVendor || s.auth.user?.topPlanner)
	const topBadgeExpiresAt = useSelector(
		(s) => s.auth.user?.topVendorExpiresAt || s.auth.user?.topPlannerExpiresAt
	)
	const badgeSubscriptionStatus = useSelector((s) => s.subscription.badgeSubscriptionStatus)

	const isBadgeTier = useMemo(
		() => (tier) => Boolean(tier && tier.startsWith('top-')),
		[]
	)
	const isLifetimeTier = useMemo(
		() => (tier) => tier === 'premium-wedding-access',
		[]
	)
	const isLegacyTier = useMemo(
		() => (tier) => {
			if (!tier) return false
			return !tier.endsWith('-month') && !isLifetimeTier(tier) && !isBadgeTier(tier)
		},
		[isBadgeTier, isLifetimeTier]
	)

	const subscriptionEndDate = useMemo(() => {
		return paymentExpiresAt || null
	}, [paymentExpiresAt])

	const subscriptionId = useMemo(() => {
		if (!subscriptionStatus || typeof subscriptionStatus !== 'object' || !payment) return null
		const matchingTier = Object.keys(subscriptionStatus).find(
			(tier) => tier === payment || (payment.endsWith('-month') && tier === payment)
		)
		return matchingTier ? subscriptionStatus[matchingTier]?.subscriptionId || null : null
	}, [subscriptionStatus, payment])

	const isRecurringSubscription = useMemo(() => {
		if (payment && payment.endsWith('-month')) {
			if (!subscriptionStatus || typeof subscriptionStatus !== 'object') return false
			const matchingTier = Object.keys(subscriptionStatus).find(
				(tier) => tier === payment
			)
			return matchingTier ? subscriptionStatus[matchingTier]?.is_recurring ?? false : false
		}
		return false
	}, [payment, subscriptionStatus])

	const badgeSubscriptionId = useMemo(() => {
		if (!isVendorOrPlanner) return null
		return badgeSubscriptionStatus?.subscriptionId || null
	}, [badgeSubscriptionStatus, isVendorOrPlanner])

	const badgeSubscriptionEndDate = useMemo(() => {
		if (!isVendorOrPlanner) return null
		return topBadgeExpiresAt || null
	}, [topBadgeExpiresAt, isVendorOrPlanner])

	const isBadgeRecurringSubscription = useMemo(() => {
		if (!isVendorOrPlanner || !topBadge) return false
		return badgeSubscriptionStatus?.is_recurring ?? false
	}, [topBadge, badgeSubscriptionStatus, isVendorOrPlanner])

	const isBadgeActive = useMemo(() => {
		if (!isVendorOrPlanner || !topBadge) return false
		if (topBadgeExpiresAt === null) return true
		return new Date(topBadgeExpiresAt) > new Date()
	}, [topBadge, topBadgeExpiresAt, isVendorOrPlanner])

	const hasAnyActivePlan = useMemo(() => {
		if (isUserLifetimePlan || payment === 'premium-wedding-access') return true

		if (!payment || payment === 'free-tier') return false

		if (payment.endsWith('-month') && paymentExpiresAt) {
			return new Date(paymentExpiresAt) > new Date()
		}

		if (isLegacyTier(payment) && paymentExpiresAt) {
			return new Date(paymentExpiresAt) > new Date()
		}

		return false
	}, [isUserLifetimePlan, payment, paymentExpiresAt, isLegacyTier])

	const formattedPlans = useMemo(() => {
		if (!accountType || !plans || !Array.isArray(plans)) return []

		const freePlan = {
			id: 'free',
			name: 'Free Tier',
			title:
				accountType === 'user'
					? 'Start planning your perfect wedding'
					: accountType === 'vendor'
						? 'Get discovered and start connecting.'
						: 'Test the wedding planning tools',
			description:
				accountType === 'user'
					? 'Perfect for couples just getting started with wedding planning.'
					: accountType === 'vendor'
						? 'Ideal for small or new vendors just starting out. Claim your listing and appear in our vendor marketplace at no cost.'
						: 'Ideal for testing the platform, early-stage planners, or part-time planners.',
			price: { amount: 0, currency: 'cad', billing: 'forever' },
			tier: 'free-tier',
		}

		const apiPlans = plans.filter((p) => {
			const isBadgePlan =
				p.planFor === 'badge' ||
				p.planFor === 'vendor-badge' ||
				p.planFor === 'planner-badge' ||
				(p.tier && p.tier.startsWith('top-')) ||
				(p.name && p.name.toLowerCase().includes('badge'))

			if (isBadgePlan) return false
			if (accountType === 'user') return p.planFor === 'user'
			if (accountType === 'vendor') return p.planFor === 'vendor'
			if (accountType === 'kazoo-planner')
				return p.planFor === 'kazoo-planner'
			return false
		})

		let badgePlan = null
		if (isVendorOrPlanner) {
			badgePlan = plans.find((p) => {
				return (
					p.planFor === 'badge' ||
					p.tier?.startsWith('top-') ||
					p.name?.toLowerCase().includes('badge') ||
					(accountType === 'vendor' &&
						(p.tier === 'top-vendor')) ||
					(accountType === 'kazoo-planner' &&
						(p.tier === 'top-planner'))
				)
			})
		}

		const formatted = [
			freePlan,
			...apiPlans.map((apiPlan) => {
				let planId = 'premium'
				let tier = 'premium-wedding-access-month'

				if (
					accountType === 'vendor' ||
					accountType === 'kazoo-planner'
				) {
					if (apiPlan.name?.toLowerCase().includes('basic')) {
						planId = 'business_basics'
						tier = 'business-basic-month'
					} else if (apiPlan.name?.toLowerCase().includes('pro')) {
						planId = 'business_pro'
						tier = 'business-pro-month'
					}
				}

				return {
					id: planId,
					subscriptionPlanId: apiPlan._id,
					name: apiPlan.name || apiPlan.title,
					title: apiPlan.title,
					description: apiPlan.description,
					price: apiPlan.price,
					tier,
					stripeProductId: apiPlan.stripeProductId,
					stripeProductPriceId: apiPlan.stripeProductPriceId,
				}
			}),
		]

		if (badgePlan && isVendorOrPlanner) {
			const badgeTier = accountType === 'vendor' ? 'top-vendor' : 'top-planner'
			formatted.push({
				id: 'top_badge',
				subscriptionPlanId: badgePlan._id,
				name: badgePlan.name || `Top ${accountType === 'vendor' ? 'Vendor' : 'Planner'} Badge`,
				title: badgePlan.title,
				description: badgePlan.description || 'Achieve premium status and stand out from the competition.',
				price: badgePlan.price,
				tier: badgePlan.tier || badgeTier,
				stripeProductId: badgePlan.stripeProductId,
				stripeProductPriceId: badgePlan.stripeProductPriceId,
			})
		}

		formatted.sort((a, b) => {
			if (a.id === 'free') return -1
			if (b.id === 'free') return 1
			if (isVendorOrPlanner) {
				const order = { business_basics: 1, business_pro: 2, top_badge: 3 }
				return (order[a.id] || 99) - (order[b.id] || 99)
			}
			return 0
		})

		return formatted
	}, [accountType, plans, isVendorOrPlanner])

	const showPaymentMethodsModal = useMemo(() => {
		const hasPaymentMethods = paymentMethods?.length > 0
		const hasNoActiveSubscription = !payment ||
			!payment.endsWith('-month') ||
			!subscriptionStatus?.subscriptionId
		const hasNoLifetimePlan = !isUserLifetimePlan
		return hasPaymentMethods && hasNoActiveSubscription && hasNoLifetimePlan
	}, [paymentMethods, payment, subscriptionStatus, isUserLifetimePlan])

	useEffect(() => {
		if (accountType) {
			dispatch(getSubscriptionPlans(accountType))
			dispatch(getPaymentMethods())
			dispatch(getActiveSubscriptionStatus())
		}
	}, [dispatch, accountType])

	return (
		<div className={styles.sspRoot}>
			<section className={styles.sspContainer}>
				<h1 className={styles.sspTitle}>Choose Your Payment plans</h1>
				<p className={styles.sspSubtitle}>
					Unlock smart automations and advanced collaboration tools to
					grow your wedding business effortlessly.
				</p>
				<div
					className={`${styles.sspGrid} ${isVendorOrPlanner ? styles.grid3 : ''}`}
				>
					{formattedPlans?.length > 0 &&
						formattedPlans.map((p) => {
							const isBadgePlan = p.id === 'top_badge'
							return (
								<PlanCard
									key={p.id}
									plan={p}
									accountType={accountType}
									subscriptionEndDate={
										isBadgePlan ? badgeSubscriptionEndDate : subscriptionEndDate
									}
									isUserLifetimePlan={isUserLifetimePlan}
									subscriptionId={
										isBadgePlan ? badgeSubscriptionId : subscriptionId
									}
									isRecurringSubscription={
										isBadgePlan
											? isBadgeRecurringSubscription
											: isRecurringSubscription
									}
									showPaymentMethodsModal={
										showPaymentMethodsModal
									}
									payment={
										isBadgePlan
											? topBadge
												? `top-${accountType === 'vendor' ? 'vendor' : 'planner'}`
												: null
											: payment
									}
									paymentExpiresAt={
										isBadgePlan ? topBadgeExpiresAt : paymentExpiresAt
									}
									hasAnyActivePlan={hasAnyActivePlan}
									isBadgeActive={isBadgePlan ? isBadgeActive : undefined}
								/>
							)
						})}
				</div>

				<PaymentMethodsModal open={modal === 'paymentMethodsModal'} />
			</section>
		</div>
	)
}

export default memo(SubscriptionPages)
